import type { Track } from '@qlk/shared';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { KhoiMuc, Nut, StatusBadge } from '../../../ui/index.js';
import { useGuiYeuCauWhitelist, useWhitelistCuaBai } from '../api.js';

export interface KhungWhitelistProps {
  /** Bài Miss Records đã đăng ký Content ID, lấy từ danh sách đang hiển thị. */
  bai: Track | undefined;
  coSua: boolean;
}

/**
 * Khung "Whitelist nhạc Miss Records" dưới bảng: liệt kê kênh còn thiếu
 * whitelist cho một bài đã đăng ký Content ID và cho gửi yêu cầu hàng loạt.
 */
export function KhungWhitelist({ bai, coSua }: KhungWhitelistProps) {
  const whitelist = useWhitelistCuaBai(bai?.id);
  const guiYeuCau = useGuiYeuCauWhitelist();
  const [daGui, setDaGui] = useState(false);

  if (!bai) return null;

  const thieu = (whitelist.data ?? []).filter((d) => d.status !== 'whitelisted');

  return (
    <KhoiMuc
      tieuDe="Whitelist nhạc Miss Records"
      moTa="Kênh chưa whitelist có thể bị Content ID claim nhầm"
      phai={
        coSua && thieu.length > 0 ? (
          <Nut
            kieu="chinh"
            dangChay={guiYeuCau.isPending}
            onClick={() => {
              guiYeuCau.mutate(
                { trackId: bai.id, channelIds: thieu.map((d) => d.channelId) },
                {
                  onSuccess: () => {
                    setDaGui(true);
                  },
                },
              );
            }}
          >
            Gửi yêu cầu whitelist
          </Nut>
        ) : undefined
      }
    >
      <p className="mb-3 text-co-ban text-chu-phu">
        Bài đang xét:{' '}
        <Link to={`/kho-nhac/${bai.id}`} className="font-semibold text-nhan">
          {bai.title}
        </Link>
      </p>

      {whitelist.isPending && <p className="text-co-ban text-chu-phu">Đang tải whitelist…</p>}

      {whitelist.error && (
        <p role="alert" className="text-co-ban text-bad-chu">
          {whitelist.error.message}
        </p>
      )}

      {!whitelist.isPending && thieu.length === 0 && (
        <p className="inline-flex items-center gap-2 text-co-ban text-ok-chu">
          <ShieldCheck size={18} aria-hidden="true" />
          Mọi kênh đang hoạt động đã được whitelist.
        </p>
      )}

      {thieu.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {thieu.map((d) => (
            <li key={d.channelId}>
              <StatusBadge tone={d.status === 'requested' ? 'warn' : 'bad'}>
                {d.channelName} · {d.status === 'requested' ? 'đã gửi yêu cầu' : 'thiếu whitelist'}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}

      {daGui && (
        <p role="status" className="mt-3 text-co-ban text-ok-chu">
          Đã gửi yêu cầu whitelist. Trạng thái chuyển sang “đã gửi yêu cầu” cho tới khi Miss Records
          xác nhận.
        </p>
      )}

      {guiYeuCau.error && (
        <p role="alert" className="mt-3 text-co-ban text-bad-chu">
          {guiYeuCau.error.message}
        </p>
      )}
    </KhoiMuc>
  );
}
